import React from 'react'
import { StringParam, useQueryParams } from 'use-query-params'
import moment from 'moment'
import { WeekStartDay } from '@retailer-platform/shared-components/src/common/date-range-picker/utils/DateRangePickerPresets.utils'
import { FilterParamsContext } from 'domains/insights-portal/utils/filter-params/filterParamsContext'
import { compactObject } from 'utils/objects/compact'
import { MomentDateParam } from 'utils/query-params/momentDate.utils'
import {
  type FilterParamsFilterNames,
  InsightsWeekStartDayLocalStorageKey,
} from 'domains/insights-portal/utils/filter-params/filterParams.types'
import { SimpleOptionsParam } from 'utils/query-params/simpleOptions.utils'
import { makeGenericStringQueryParamEncoder } from 'utils/query-params/string.utils'

const defaultDates = {
  dateRange: 'last_7_days',
  from: moment().subtract(1, 'week').startOf('day'),
  to: moment().subtract(1, 'days').endOf('day'),
}

export const defaultFiltersValues = {
  weekStartDay: WeekStartDay.Monday,
  comparisonType: 'lastPeriod',
  retailer: [],
  state: [],
  region: [],
  storeLocation: [],
  storeLocationCode: [],
  orderSource: [],
  fulfillmentType: [],
  alcohol: [],
  express: [],
  storeGroup: [],
  convenience: [],
  firstOrder: [],
  ebt: [],
  loyalty: [],
  L1Category: [],
  L2Category: [],
  L3Category: [],
  L4Category: [],
  L5Category: [],
  L6Category: [],
  partnerDivision: [],
  orderCurrency: '',
  popularSearchTerm: [],
  campaignStatus: [],
  campaignName: [],
  campaignEndingSoon: [],
  campaignType: [],
  demandSource: [],
  campaign: [],
  collectionId: [],
  platform: [],
}

export const useFilterParams = () => {
  const [selectedFiltersValues, setSelectedFiltersValues] = useQueryParams({
    retailer: SimpleOptionsParam,
    state: SimpleOptionsParam,
    region: SimpleOptionsParam,
    storeLocation: SimpleOptionsParam,
    storeLocationCode: SimpleOptionsParam,
    storeGroup: SimpleOptionsParam,
    orderSource: SimpleOptionsParam,
    fulfillmentType: SimpleOptionsParam,
    weekStartDay: makeGenericStringQueryParamEncoder<WeekStartDay>(),
    loyalty: SimpleOptionsParam,
    express: SimpleOptionsParam,
    firstOrder: SimpleOptionsParam,
    ebt: SimpleOptionsParam,
    alcohol: SimpleOptionsParam,
    convenience: SimpleOptionsParam,
    L1Category: SimpleOptionsParam,
    L2Category: SimpleOptionsParam,
    L3Category: SimpleOptionsParam,
    L4Category: SimpleOptionsParam,
    L5Category: SimpleOptionsParam,
    L6Category: SimpleOptionsParam,
    comparisonType: makeGenericStringQueryParamEncoder<'lastPeriod' | 'lastYear'>(),
    popularSearchTerm: SimpleOptionsParam,
    partnerDivision: SimpleOptionsParam,
    orderCurrency: StringParam,
    campaignStatus: SimpleOptionsParam,
    campaignName: SimpleOptionsParam,
    campaignEndingSoon: SimpleOptionsParam,
    campaignType: SimpleOptionsParam,
    demandSource: SimpleOptionsParam,
    campaign: SimpleOptionsParam,
    collectionId: SimpleOptionsParam,
    platform: SimpleOptionsParam,
  })

  // add params that need a default here
  const selectedFiltersValuesWithDefaults: typeof selectedFiltersValues = {
    ...defaultFiltersValues,
    weekStartDay: localStorage.getItem(InsightsWeekStartDayLocalStorageKey) || WeekStartDay.Monday,
    ...compactObject(selectedFiltersValues),
  }
  const [datesState, setDatesState] = useQueryParams({
    dateRange: makeGenericStringQueryParamEncoder<string>(),
    from: MomentDateParam,
    to: MomentDateParam,
  })

  const dates = {
    ...defaultDates,
    ...compactObject(datesState),
  }

  type FilterParamsSelections = Parameters<typeof setSelectedFiltersValues>[0]

  function setFilterSelection<TName extends FilterParamsFilterNames>(
    name: TName,
    selection: FilterParamsSelections[TName]
  ) {
    if (name === 'weekStartDay') {
      localStorage.setItem(InsightsWeekStartDayLocalStorageKey, selection as string)
    }

    setSelectedFiltersValues({ [name]: selection })
  }

  return {
    setFilterSelection,
    setSelectedFiltersValues,
    selectedFiltersValues: selectedFiltersValuesWithDefaults,
    dates,
    setDates: setDatesState,
  }
}

export const FilterParamsProvider: React.FunctionComponent = ({ children }) => {
  const filterParamsState = useFilterParams()

  return (
    <FilterParamsContext.Provider value={filterParamsState}>
      {children}
    </FilterParamsContext.Provider>
  )
}
